import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconSearch } from '@u21/tabler-icons';

// Selectors
import { selectHasReadCasesPermissions } from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatCaseOptions } from 'app/modules/search/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { CaseSearchPayload } from 'app/modules/search/payload';
import {
  searchCase,
  useCaseSearch,
} from 'app/modules/cases/queries/useCaseSearch';
import { CASE_QUERY_KEYS } from 'app/modules/cases/queries/keys';
import { getCaseSearchConditions } from 'app/modules/search/utils';

export type U21CaseSearchProps<TClearable extends boolean> = DistributiveOmit<
  U21SelectSearchProps<number, TClearable>,
  'options' | 'onSearch'
>;

export const U21CaseSearch = <TClearable extends boolean>(
  props: U21CaseSearchProps<TClearable>,
) => {
  const { value } = props;
  const queryClient = useQueryClient();

  const [payload, setPayload] = useState<CaseSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');
  const hasReadCasesPermissions = useSelector(selectHasReadCasesPermissions);

  const {
    cases: data,
    query: { isFetching: loading },
  } = useCaseSearch(payload);

  const options = useMemo(() => formatCaseOptions(data), [data]);

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (hasReadCasesPermissions) {
      let missingValues: number[] = [];

      if (Array.isArray(value)) {
        missingValues = value.filter(
          (i) => !options.find((j) => j.value === i),
        );
      } else if (value !== undefined) {
        const valueExists = options.find((i) => i.value === value);
        if (!valueExists) {
          missingValues = [value];
        }
      }

      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;

        const initialPayload: CaseSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: getCaseSearchConditions(missingValues.map(String)),
        };

        queryClient.prefetchQuery({
          queryKey: CASE_QUERY_KEYS.getCaseSearch(initialPayload),
          queryFn: () => searchCase(initialPayload),
        });

        setPayload(initialPayload);
      }
    }
  }, [hasReadCasesPermissions, options, queryClient, value]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        setPayload({
          offset: OFFSET_DEFAULT,
          limit: INFINITE_SCROLL_LIMIT,
          search_conditions: getCaseSearchConditions([query]),
        });
      }
    },

    [],
  );

  if (!hasReadCasesPermissions) {
    return null;
  }

  return (
    <U21SelectSearch
      loading={loading}
      options={options}
      onSearch={onSearch}
      startIcon={<IconSearch />}
      noOptionsText={searchValue ? 'No results found' : 'Type to search'}
      {...props}
    />
  );
};
